import { LocationOutlined } from '@teko/ds-icon';
import { Button, Flex, Form, Input, Shape, styled } from '@teko/ds-mobile';
import { Select } from '@teko/ecommerce/mobile';
import { ThemeProps } from '@teko/ui';
import { useDistanceTerminalContext } from 'contexts';
import { tSelectPlaceholder } from 'helpers/translate';
import {
  useLocations,
  useLocationsV2,
  useSuggestionAddress,
  useTenantConfig,
} from 'hooks';
import { t } from 'i18n';
import { useEffect, useState } from 'react';
import { useGetDistanceTerminal } from '../useGetDistanceTerminal';
import { SwitchGetByGeoLocation } from './SwitchGetByGeoLocation';

interface AddressForm {
  address: string;
  provinceCode: string;
  wardCode: string;
  districtCode?: string;
}
const COUNT_STRING_WITHOUT_STREET_ADDRESS = 4;

export const ModeEnterAddress = () => {
  const { epicOmni1589 } = useTenantConfig();

  return (
    <>
      <SwitchGetByGeoLocation />
      {epicOmni1589 ? <FormAddressV2 /> : <FormAddressV1 />}
    </>
  );
};

const Wrapper = styled.div`
  height: 420px;
`;

const SubTitle = styled.span`
  font-size: 14px;
  font-weight: 400;
`;

const StyledSelect = styled(Select)`
  margin-top: 16px;
  input {
    border-radius: 4px;
  }
`;

const StyledForm = styled(Form)`
  width: 100%;
`;

const StyledButtonWrapper = styled(Shape)`
  width: 100%;
  position: fixed;
  bottom: 16px;
  padding-left: 16px;
  padding-right: 16px;
`;

const Error = styled.div(
  ({ theme }: ThemeProps) => `
    padding-top: 0;
    color: ${theme?.colors.error500};
    font-size: 0.75rem;
    line-height: 1rem;
    transition: color 0.4s cubic-bezier(0.215, 0.61, 0.355, 1);
    margin-top: 0.5rem;
  `,
);
export const SuggestWrapper = styled.div`
  background-color: #fafafa;
  border-radius: 12px;
  padding: 8px;
  margin-bottom: 15px;
  max-height: 200px;
  overflow-y: scroll;
`;

export const ItemSuggestion = styled.div`
  padding: 8px 10px;
  cursor: pointer;
`;

const Text = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #434657;
`;
export const Spinner = styled.div<ThemeProps>(
  ({ theme }) => `
  border: 3px solid ${theme.colors.blue100};
  border-top-color: ${theme.colors.blue500};
  border-radius: 50%;
  width: 1.5em;
  height: 1.5em;
  margin: 0 0.5rem;
  animation: spin 1s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`,
);
const FormAddressV1 = () => {
  const [form] = Form.useForm<AddressForm>();
  const [missingRequiredField, setMissingRequiredField] = useState({
    province: '',
    ward: '',
    district: '',
    streetAddress: '',
  });
  const [place, setPlace] = useState<{ lat?: number; long?: number }>({
    lat: undefined,
    long: undefined,
  });
  const { epicOmni1825SuggestAddress } = useTenantConfig();
  const {
    locationGetDistanceTerminals,
    isLoadingGetDistanceTerminals,
    messageError,
    isGetCurrentLocation,
  } = useDistanceTerminalContext();
  const { getDistanceTerminalsByGeoLocation, getDistanceTerminalsByAddress } =
    useGetDistanceTerminal();

  const {
    provinces = [],
    wards = [],
    province,
    ward,
    onProvinceChanged,
    onWardChanged,
    district,
    districts = [],
    onDistrictChanged,
    setLocation,
  } = useLocations({
    provinceCode: locationGetDistanceTerminals?.provinceCode,
    provinceName: locationGetDistanceTerminals?.provinceName,
    districtCode: locationGetDistanceTerminals?.districtCode,
    districtName: locationGetDistanceTerminals?.districtName,
    wardCode: locationGetDistanceTerminals?.wardCode,
    wardName: locationGetDistanceTerminals?.wardName,
  });

  const getDistanceTerminalsByAddressForm = async () => {
    const formData = await form.validateFields();

    if (
      !province?.code ||
      !ward?.code ||
      !district?.code ||
      !formData.address
    ) {
      return setMissingRequiredField({
        province: !province?.code
          ? t('Tỉnh/Thành phố không được bỏ trống')
          : '',
        ward: !ward?.code ? t('Phường/Xã không được bỏ trống') : '',
        district: !district?.code ? t('Quận/Huyện không được bỏ trống') : '',
        streetAddress: !formData.address
          ? t('Địa chỉ chi tiết không được bỏ trống')
          : '',
      });
    }
    const provinceName =
      provinces?.find((p) => p.code === province?.code)?.name || '';
    const districtName =
      districts?.find((d) => d.code === district?.code)?.name || '';
    const wardName = wards?.find((w) => w.code === ward.code)?.name || '';

    getDistanceTerminalsByAddress({
      ...formData,
      provinceName,
      wardName,
      provinceCode: province.code,
      wardCode: ward.code,
      districtName,
      districtCode: district.code,
      lat: place.lat,
      long: place.long,
    });
  };

  useEffect(() => {
    if (!!locationGetDistanceTerminals) {
      form.setFieldsValue({
        provinceCode: locationGetDistanceTerminals.provinceCode || '',
        wardCode: locationGetDistanceTerminals.wardCode || '',
        districtCode: locationGetDistanceTerminals.districtCode || '',
        address: locationGetDistanceTerminals.address || '',
      });
      setLocation({
        provinceCode: locationGetDistanceTerminals.provinceCode,
        districtCode: locationGetDistanceTerminals.districtCode || '',
        wardCode: locationGetDistanceTerminals.wardCode,
      });
      setPlace({
        lat: locationGetDistanceTerminals.lat,
        long: locationGetDistanceTerminals.long,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationGetDistanceTerminals, JSON.stringify(provinces)]);

  const {
    dataSuggestion,
    setSuggestInputText,
    openListSuggestion,
    setOpenListSuggestion,
    isLoadingSuggest,
    getPlaceDetails,
  } = useSuggestionAddress({
    province: province?.name || '',
    district: district?.name || '',
    ward: ward?.name || '',
  });

  const onClickSuggest = async (placeId: string) => {
    const items = await getPlaceDetails(placeId);
    if (!items || !Array.isArray(items) || !items.length) {
      return;
    }
    // items là danh sách các placeDetail và cần lấy placeDetail đầu tiên
    // trong case này chỉ gửi 1 placeID lên nên chỉ có 1 placeDetail
    const item = items?.[0];
    const address =
      item.fullAddress
        .split(',')
        ?.splice(
          0,
          item.fullAddress.split(',').length -
            COUNT_STRING_WITHOUT_STREET_ADDRESS,
        )
        ?.join(',') ||
      item.fullAddress ||
      '';
    setOpenListSuggestion(false);
    setPlace({ lat: item.lat, long: item.long });
    const newValue = {
      provinceCode: item.provinceCode || undefined,
      ...(item.districtCode ? { districtCode: item.districtCode } : {}),
      ...(item.wardCode ? { wardCode: item.wardCode } : {}),
    };
    form.setFieldsValue({
      address,
      ...newValue,
    });
    if (!!item.provinceCode && !!item.districtCode && !!item.wardCode) {
      setLocation({
        provinceCode: item.provinceCode,
        districtCode: item.districtCode,
        wardCode: item.wardCode,
      });
    }
  };

  const resetPosition = () => {
    setPlace({ lat: undefined, long: undefined });
  };

  //
  return (
    <>
      <StyledForm layout="vertical" form={form}>
        <Wrapper style={{ overflowY: 'scroll' }}>
          <>
            <SubTitle style={{ textAlign: 'left' }}>
              {t('Hoặc nhập địa chỉ')}
            </SubTitle>

            <StyledSelect
              title={t('Tỉnh/Thành phố')}
              wrapperBottomSheetHeight="100dvh"
              placeholder={tSelectPlaceholder('Tỉnh/Thành phố')}
              haveFilter
              popupTitle={t('Tỉnh/Thành phố')}
              selectKey="text"
              required
              disabled={isGetCurrentLocation}
              items={provinces?.map(({ name, code }) => ({
                text: name,
                value: code,
              }))}
              value={
                province?.name ||
                provinces?.find(
                  (p) => p.code === locationGetDistanceTerminals?.provinceCode,
                )?.name ||
                ''
              }
              onSelectChange={({ value }) => {
                const location = provinces?.find((prov) => prov.code === value);
                if (location) {
                  onProvinceChanged(location.code);
                  form.setFieldsValue({
                    provinceCode: location.code,
                  });
                  resetPosition();
                }
              }}
              error={missingRequiredField.province}
            />
            <StyledSelect
              title={t('Quận/Huyện')}
              wrapperBottomSheetHeight="100dvh"
              placeholder={tSelectPlaceholder('Quận/Huyện')}
              haveFilter
              popupTitle={t('Quận/Huyện')}
              selectKey="text"
              disabled={isGetCurrentLocation}
              items={districts?.map(({ name, code }) => ({
                text: name,
                value: code,
              }))}
              required
              value={
                district?.name ||
                districts?.find(
                  (p) => p.code === locationGetDistanceTerminals?.districtCode,
                )?.name ||
                ''
              }
              onSelectChange={({ value }) => {
                const location = districts?.find((ward) => ward.code === value);
                if (location) {
                  onDistrictChanged(location.code);
                  form.setFieldsValue({
                    districtCode: location.code,
                  });
                  resetPosition();
                }
              }}
              error={missingRequiredField.district}
            />

            <StyledSelect
              title={t('Phường/Xã')}
              wrapperBottomSheetHeight="100dvh"
              placeholder={tSelectPlaceholder('Phường/Xã')}
              haveFilter
              popupTitle={t('Phường/Xã')}
              selectKey="text"
              disabled={isGetCurrentLocation}
              items={wards?.map(({ name, code }) => ({
                text: name,
                value: code,
              }))}
              required
              value={
                ward?.name ||
                wards?.find(
                  (p) => p.code === locationGetDistanceTerminals?.wardCode,
                )?.name ||
                ''
              }
              onSelectChange={({ value }) => {
                const location = wards?.find((ward) => ward.code === value);
                if (location) {
                  onWardChanged(location.code);
                  form.setFieldsValue({
                    wardCode: location.code,
                  });
                  resetPosition();
                }
              }}
              error={missingRequiredField.ward}
            />

            <Form.Item label={t('Địa chỉ cụ thể')} name="address" required>
              <Input
                disabled={
                  isGetCurrentLocation ||
                  ((!province?.code || !district?.code || !ward?.code) &&
                    epicOmni1825SuggestAddress)
                }
                placeholder={t('Số nhà, ngõ, tên đường...')}
                onChange={(text) => {
                  setSuggestInputText(text);
                  setOpenListSuggestion(true);
                  resetPosition();
                }}
              />
            </Form.Item>
            {!!missingRequiredField.streetAddress && (
              <Error>{missingRequiredField.streetAddress}</Error>
            )}
          </>
          {epicOmni1825SuggestAddress &&
            !!dataSuggestion &&
            !!openListSuggestion &&
            (!!dataSuggestion.length || isLoadingSuggest) && (
              <SuggestWrapper>
                {isLoadingSuggest ? (
                  <Flex justify="center" align="center">
                    <Spinner />
                  </Flex>
                ) : (
                  <>
                    <Text style={{ textAlign: 'left' }}>{t('Gợi ý')}</Text>
                    {dataSuggestion.map((item) => {
                      return (
                        <ItemSuggestion
                          key={item.fullAddress}
                          onClick={() => onClickSuggest(item.placeId)}
                        >
                          <LocationOutlined />
                          <Text style={{ textAlign: 'left', marginLeft: 8 }}>
                            {item.fullAddress}
                          </Text>
                        </ItemSuggestion>
                      );
                    })}
                  </>
                )}
              </SuggestWrapper>
            )}
          {!!messageError && (
            <Shape
              width="100%"
              borderRadius="xl"
              border="1px solid warning"
              backgroundColor="pending100"
              padding="s8"
            >
              {messageError}
            </Shape>
          )}
        </Wrapper>
      </StyledForm>
      <StyledButtonWrapper>
        <Button
          disabled={isLoadingGetDistanceTerminals}
          block
          type="submit"
          variant="primary"
          onClick={
            isGetCurrentLocation
              ? getDistanceTerminalsByGeoLocation
              : getDistanceTerminalsByAddressForm
          }
          width={'100%'}
        >
          {t('Xác nhận')}
        </Button>
      </StyledButtonWrapper>
    </>
  );
};

const FormAddressV2 = () => {
  const [form] = Form.useForm<AddressForm>();
  const [missingRequiredField, setMissingRequiredField] = useState({
    province: '',
    ward: '',
    streetAddress: '',
  });
  const [place, setPlace] = useState<{ lat?: number; long?: number }>({
    lat: undefined,
    long: undefined,
  });
  const {
    locationGetDistanceTerminals,
    isLoadingGetDistanceTerminals,
    messageError,
    isGetCurrentLocation,
  } = useDistanceTerminalContext();
  const { getDistanceTerminalsByGeoLocation, getDistanceTerminalsByAddress } =
    useGetDistanceTerminal();
  const { epicOmni1825SuggestAddress } = useTenantConfig();
  const {
    provinces = [],
    wards = [],
    province,
    ward,
    onProvinceChanged,
    onWardChanged,
  } = useLocationsV2(
    locationGetDistanceTerminals?.provinceCode,
    locationGetDistanceTerminals?.wardCode,
  );

  const getDistanceTerminalsByAddressForm = async () => {
    const formData = await form.validateFields();
    if (!province?.code || !ward?.code || !formData.address) {
      return setMissingRequiredField({
        province: !province?.code
          ? t('Tỉnh/Thành phố không được bỏ trống')
          : '',
        ward: !ward?.code ? t('Phường/Xã không được bỏ trống') : '',
        streetAddress: !formData.address
          ? t('Địa chỉ chi tiết không được bỏ trống')
          : '',
      });
    }
    const provinceName =
      provinces.find((p) => p.code === province?.code)?.title || '';

    const wardName = wards.find((w) => w.code === ward.code)?.title || '';
    getDistanceTerminalsByAddress({
      ...formData,
      provinceName,
      wardName,
      provinceCode: province.code,
      wardCode: ward.code,
      lat: place.lat,
      long: place.long,
    });
  };
  const {
    dataSuggestion,
    setSuggestInputText,
    openListSuggestion,
    setOpenListSuggestion,
    isLoadingSuggest,
    getPlaceDetails,
  } = useSuggestionAddress({
    province: province?.title || '',
    ward: ward?.title || '',
  });

  useEffect(() => {
    if (!!locationGetDistanceTerminals) {
      form.setFieldsValue({
        provinceCode: locationGetDistanceTerminals.provinceCode || '',
        wardCode: locationGetDistanceTerminals.wardCode || '',
        address: locationGetDistanceTerminals.address || '',
      });
      onProvinceChanged(locationGetDistanceTerminals.provinceCode);
      onWardChanged(locationGetDistanceTerminals.wardCode);
      setPlace({
        lat: locationGetDistanceTerminals.lat,
        long: locationGetDistanceTerminals.long,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationGetDistanceTerminals]);

  const onClickSuggest = async (placeId: string) => {
    const items = await getPlaceDetails(placeId);
    if (!items || !Array.isArray(items) || !items.length) {
      return;
    }
    // items là danh sách các placeDetail và cần lấy placeDetail đầu tiên
    // trong case này chỉ gửi 1 placeID lên nên chỉ có 1 placeDetail
    const item = items?.[0];
    const address =
      item.fullAddress
        .split(',')
        ?.splice(
          0,
          item.fullAddress.split(',').length -
            COUNT_STRING_WITHOUT_STREET_ADDRESS,
        )
        ?.join(',') ||
      item.fullAddress ||
      '';
    setOpenListSuggestion(false);
    setPlace({ lat: item.lat, long: item.long });
    const newValue = {
      provinceCode: item.provinceCode || undefined,
      ...(item.wardCode ? { wardCode: item.wardCode } : {}),
    };
    form.setFieldsValue({
      address,
      ...newValue,
    });
  };

  const resetPosition = () => {
    setPlace({ lat: undefined, long: undefined });
  };

  return (
    <>
      <StyledForm layout="vertical" form={form}>
        <Wrapper>
          <>
            <SubTitle style={{ textAlign: 'left' }}>
              {t('Hoặc nhập địa chỉ ')}
            </SubTitle>
            <StyledSelect
              title={t('Tỉnh/Thành phố')}
              wrapperBottomSheetHeight="100dvh"
              placeholder={tSelectPlaceholder('Tỉnh/Thành phố')}
              haveFilter
              popupTitle={t('Tỉnh/Thành phố')}
              selectKey="text"
              required
              disabled={isGetCurrentLocation}
              items={provinces?.map(({ title, code }) => ({
                text: title,
                value: code,
              }))}
              value={
                province?.title ||
                provinces?.find(
                  (p) => p.code === locationGetDistanceTerminals?.provinceCode,
                )?.title ||
                ''
              }
              onSelectChange={({ value }) => {
                const location = provinces?.find((prov) => prov.code === value);
                if (location) {
                  onProvinceChanged(location.code);
                  form.setFieldsValue({
                    provinceCode: location.code,
                  });
                  resetPosition();
                }
              }}
              error={missingRequiredField.province}
            />
            <StyledSelect
              title={t('Phường/Xã')}
              wrapperBottomSheetHeight="100dvh"
              placeholder={tSelectPlaceholder('Phường/Xã')}
              haveFilter
              disabled={isGetCurrentLocation}
              popupTitle={t('Phường/Xã')}
              selectKey="text"
              items={wards?.map(({ title, code }) => ({
                text: title,
                value: code,
              }))}
              required
              value={
                ward?.title ||
                wards?.find(
                  (p) => p.code === locationGetDistanceTerminals?.wardCode,
                )?.title ||
                ''
              }
              onSelectChange={({ value }) => {
                const location = wards?.find((ward) => ward.code === value);
                if (location) {
                  onWardChanged(location.code);
                  form.setFieldsValue({
                    wardCode: location.code,
                  });
                  resetPosition();
                }
              }}
              error={missingRequiredField.ward}
            />
            <Form.Item required label={t('Địa chỉ cụ thể')} name="address">
              <Input
                disabled={isGetCurrentLocation}
                placeholder={t('Số nhà, ngõ, tên đường...')}
                onChange={(text) => {
                  setSuggestInputText(text);
                  setOpenListSuggestion(true);
                }}
              />
            </Form.Item>
            {!!missingRequiredField.streetAddress && (
              <Error>{missingRequiredField.streetAddress}</Error>
            )}
          </>
          {epicOmni1825SuggestAddress &&
            !!dataSuggestion &&
            !!openListSuggestion &&
            (!!dataSuggestion.length || isLoadingSuggest) && (
              <SuggestWrapper>
                {isLoadingSuggest ? (
                  <Flex justify="center" align="center">
                    <Spinner />
                  </Flex>
                ) : (
                  <>
                    <Text style={{ textAlign: 'left' }}>{t('Gợi ý')}</Text>
                    {dataSuggestion.map((item) => {
                      return (
                        <ItemSuggestion
                          key={item.fullAddress}
                          onClick={() => onClickSuggest(item.placeId)}
                        >
                          <LocationOutlined />
                          <Text style={{ textAlign: 'left', marginLeft: 8 }}>
                            {item.fullAddress}
                          </Text>
                        </ItemSuggestion>
                      );
                    })}
                  </>
                )}
              </SuggestWrapper>
            )}
          {!!messageError && (
            <Shape
              width="100%"
              borderRadius="xl"
              border="1px solid warning"
              backgroundColor="pending100"
              padding="s8"
            >
              {messageError}
            </Shape>
          )}
        </Wrapper>
      </StyledForm>
      <StyledButtonWrapper>
        <Button
          disabled={isLoadingGetDistanceTerminals}
          block
          type="submit"
          variant="primary"
          onClick={
            isGetCurrentLocation
              ? getDistanceTerminalsByGeoLocation
              : getDistanceTerminalsByAddressForm
          }
          width={'100%'}
        >
          {t('Xác nhận')}
        </Button>
      </StyledButtonWrapper>
    </>
  );
};
